import type { ReactNode } from "react";
import { cx } from "@/lib/format";
import { ApiError, NetworkError } from "@/lib/api";
import { Button } from "./Button";
import { Handle } from "./Chip";

/**
 * The three things a screen is when it has no content: waiting, empty, or
 * broken. All three are designed, because a screen that has not decided what
 * it looks like while loading is a screen that flashes.
 */

/**
 * A placeholder with the shape of what is coming.
 *
 * The sweep runs left to right once every 1.4s and is a 12% band rather than
 * a white one — on the dark card a white sweep is a flashbulb.
 */
export function Skeleton({ className, rounded = "md" }: { className?: string; rounded?: "sm" | "md" | "lg" | "full" }) {
  return (
    <div
      className={cx(
        "relative overflow-hidden bg-[var(--surface-2)]",
        rounded === "sm" && "rounded-[var(--radius-sm)]",
        rounded === "md" && "rounded-[var(--radius-sm)]",
        rounded === "lg" && "rounded-[var(--radius)]",
        rounded === "full" && "rounded-full",
        className,
      )}
      aria-hidden
    >
      <div
        className={cx(
          "absolute inset-0 animate-sweep",
          "bg-gradient-to-r from-transparent via-[color-mix(in_oklab,var(--text-primary)_12%,transparent)] to-transparent",
        )}
      />
    </div>
  );
}

/**
 * A table that has not arrived.
 *
 * The row heights match the real table's, so the content does not
 * jump when it lands. That is the only job of a skeleton and it is the one
 * most of them get wrong.
 */
export function TableSkeleton({ rows = 6, columns = 4, lamps = true }: { rows?: number; columns?: number; lamps?: boolean }) {
  return (
    <div role="status" aria-label="Loading">
      <div className="h-8 border-b border-[var(--border)] bg-[var(--surface-2)]" />
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className="flex h-[var(--h-row)] items-center gap-3 border-b border-[var(--border)] px-3"
        >
          {lamps && <Skeleton className="h-1.5 w-1.5" rounded="full" />}
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton
              key={c}
              className={cx("h-3", c === 0 ? "w-40" : c === columns - 1 ? "ml-auto w-16" : "w-24")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Nothing here yet.
 *
 * An empty state carries one sentence saying what would be here and one
 * control that puts something here. No illustration: a drawing of an empty
 * box tells somebody who already knows the box is empty that the box is
 * empty.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
  framed,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  /** A lucide glyph, on a soft accent plate. */
  icon?: ReactNode;
  /** Draws its own dashed border. For an empty state that is not in a card. */
  framed?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cx("px-6 py-12 text-center", framed && "rounded-xl", className)}
      style={
        framed
          ? { backgroundColor: "var(--surface)", border: "1px dashed var(--border-strong)" }
          : undefined
      }
    >
      {icon && (
        <div
          className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-[var(--border)]"
          style={{ backgroundColor: "var(--primary-soft)", color: "var(--primary)" }}
        >
          {icon}
        </div>
      )}
      <h3 className="t-sub mb-1.5">{title}</h3>
      {description && (
        <p className="t-body mx-auto max-w-sm leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-5 inline-flex">{action}</div>}
    </div>
  );
}

export interface DescribedError {
  title: string;
  message: string;
  /** The server's reference for this failure. Empty when there is none. */
  requestId: string;
  retryable: boolean;
}

/**
 * Any thrown value, turned into something worth showing a person.
 *
 * The API's own error carries a machine code, a sentence written for this
 * situation, a request id and whether retrying could possibly help. All four
 * are used: the code picks the heading, the sentence is shown as-is rather
 * than replaced by "Something went wrong", the id is offered because support
 * will ask for it, and the retry flag decides whether a Try again button
 * appears at all. A button that cannot work is worse than no button.
 */
export function describeError(error: unknown): DescribedError {
  if (error instanceof ApiError) {
    return {
      title: titleForCode(error.code),
      message: error.message,
      requestId: error.requestId,
      retryable: error.isRetryable,
    };
  }
  if (error instanceof NetworkError) {
    return {
      title: "Connection lost",
      message: error.message,
      requestId: "",
      retryable: true,
    };
  }
  return {
    title: "Something went wrong",
    message: "An unexpected problem occurred. Please try again.",
    requestId: "",
    retryable: true,
  };
}

function titleForCode(code: string): string {
  switch (code) {
    case "not_found":
      return "Not found";
    case "permission_denied":
      return "You do not have access";
    case "unauthenticated":
      return "Please sign in";
    case "account_suspended":
      return "Account suspended";
    case "rate_limited":
      return "Too many requests";
    case "invalid_argument":
      return "Check the details";
    case "already_exists":
      return "Already exists";
    case "conflict":
      return "Conflicting change";
    case "temporarily_unavailable":
      return "Temporarily unavailable";
    case "not_supported_in_this_environment":
      return "Not available here";
    default:
      return "Something went wrong";
  }
}

/**
 * A screen, or a card, that could not load.
 *
 * The reference id is a Handle rather than text, because the only reason it is
 * on the screen is that somebody is about to paste it into a support ticket.
 */
export function ErrorState({
  error,
  onRetry,
  compact,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  /** Inside a card, where the screen around it is fine. */
  compact?: boolean;
  className?: string;
}) {
  const it = describeError(error);
  return (
    <div
      role="alert"
      className={cx(
        "flex flex-col items-start gap-3 rounded-[var(--radius)]",
        compact ? "px-3 py-3" : "px-4 py-8",
        "border border-[color-mix(in_oklab,var(--danger)_28%,transparent)]",
        "bg-[color-mix(in_oklab,var(--danger)_5%,transparent)]",
        className,
      )}
    >
      <span className="flex items-center gap-2">
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ background: "var(--danger)" }}
          aria-hidden
        />
        <span className="t-strong">{it.title}</span>
      </span>

      <p className="t-meta max-w-[64ch] leading-5">{it.message}</p>

      <div className="flex flex-wrap items-center gap-2">
        {it.retryable && onRetry && (
          <Button size="dense" onClick={onRetry}>
            Try again
          </Button>
        )}
        {it.requestId && <Handle value={it.requestId} label="Reference" />}
      </div>
    </div>
  );
}
